import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useParams, useSearchParams, useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { useAuth } from "../scripts/AuthContext";
import { useOrgFilter } from "../scripts/OrgFilterContext";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { API_BASE_URL } from "@/lib/apiBase";
import { resolveAvatarUrl } from "@/lib/avatarApi";
import { toastError, toastSuccess } from "@/lib/toast";
import { pathWithPostAnchor, permalinkForSocialPost } from "@/lib/permalinks";
import { useSocialPaths } from "@/lib/socialPaths";
import { useSocialWriteApis } from "@/lib/socialWriteApis";
import { listSocialSavedPosts } from "@/lib/socialSavedApi";
import { markdownToPlain } from "@/lib/markdownPlain";
import {
  mergeLikedFromPosts,
  seedLikedFromPosts,
} from "@/lib/socialFeedLikes";
import {
  uploadSocialPostMedia,
  setSocialEmbed,
  removeSocialPostMedia,
  resolveMediaUrl,
} from "@/lib/socialMedia";
import PostComposer from "./PostComposer";
import PostCard from "./PostCard";
import { useConfirm } from "@/components/ConfirmDialogProvider";
import SocialEditProfileDialog from "./SocialEditProfileDialog";
import SocialFollowListDialog from "./SocialFollowListDialog";
import { normalizeSocialFeedPost } from "@/lib/normalizeSocialFeedPost";

/**
 * Twitter-style /u/:handle — cover, avatar, bio, Posts timeline, edit modal (self).
 */
const SocialProfilePage = () => {
  const { handle } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const { accessToken, user } = useAuth();
  const { selectedOrgId } = useOrgFilter();
  const isSocialWorkspace =
    selectedOrgId === "social" ||
    selectedOrgId === null ||
    selectedOrgId === "";
  const hasOrgSelected = !isSocialWorkspace;
  const navigate = useNavigate();
  const location = useLocation();
  const confirm = useConfirm();
  const paths = useSocialPaths();
  const writeApis = useSocialWriteApis();

  const [activeTab, setActiveTab] = useState(() => {
    const tab = searchParams.get("tab");
    return tab === "saved" || tab === "drafts" ? tab : "posts";
  });

  const [profile, setProfile] = useState(null);
  const [orgRelation, setOrgRelation] = useState(null); // { is_following, can_follow }
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [followListMode, setFollowListMode] = useState(null); // "followers" | "following" | null

  const [posts, setPosts] = useState([]);
  const [postsLoading, setPostsLoading] = useState(false);
  const [postsError, setPostsError] = useState(null);
  const [hasMore, setHasMore] = useState(false);
  const [likedPosts, setLikedPosts] = useState(() => new Set());

  const [savedPosts, setSavedPosts] = useState([]);
  const [savedPostsTotal, setSavedPostsTotal] = useState(0);
  const [savedPostsLoading, setSavedPostsLoading] = useState(false);
  const [savedPostsError, setSavedPostsError] = useState(null);

  const [drafts, setDrafts] = useState([]);
  const [draftsTotal, setDraftsTotal] = useState(0);
  const [draftsLoading, setDraftsLoading] = useState(false);
  const [draftsError, setDraftsError] = useState(null);
  const [editingDraftId, setEditingDraftId] = useState(null);
  const [draftComposerKey, setDraftComposerKey] = useState(0);
  const [draftActionLoading, setDraftActionLoading] = useState(false);

  const openThread = useCallback(
    (post) => {
      const postId = post?.post_id ?? post?.social_post_id;
      if (postId == null) return;
      const returnTo = pathWithPostAnchor(
        `${location.pathname}${location.search || ""}`,
        postId
      );
      navigate(paths.thread({ postId, returnPath: returnTo }));
    },
    [navigate, location.pathname, location.search, paths]
  );

  const normalizePost = useCallback((p) => normalizeSocialFeedPost(p), []);

  const quoteEmbedRoot = (post) => {
    if (post?.repost_kind === "quote" && post.repost_of) return post.repost_of;
    if (
      post?.repost_kind === "bare" &&
      post.repost_of?.repost_kind === "quote" &&
      post.repost_of.repost_of
    ) {
      return post.repost_of.repost_of;
    }
    return null;
  };

  const toggleSocialLike = async (postId) => {
    if (!accessToken || postId == null) return;
    if (writeApis.isActAsOrg && !writeApis.allowEngage) return;
    const id = String(postId);
    const already = likedPosts.has(id);
    if (already) {
      if (writeApis.isActAsOrg) return;
      const list = await axios.get(
        `${API_BASE_URL}/social/posts/${id}/interactions`,
        { headers: { Authorization: `Bearer ${accessToken}` } }
      );
      const mine = (list.data || []).find(
        (i) =>
          (i.interaction_type || i.interactionType) === "like" &&
          !(i.parent_id ?? i.parentId) &&
          (i.user_id || i.userId) === user?.user_id
      );
      if (mine?.interaction_id || mine?.interactionId) {
        await axios.delete(
          `${API_BASE_URL}/social/posts/interactions/${
            mine.interaction_id || mine.interactionId
          }`,
          { headers: { Authorization: `Bearer ${accessToken}` } }
        );
      }
      setLikedPosts((prev) => {
        const next = new Set(prev);
        next.delete(id);
        return next;
      });
      return;
    }
    await axios.post(
      writeApis.interactionsUrl(id),
      { interaction_type: "like" },
      { headers: { Authorization: `Bearer ${accessToken}` } }
    );
    setLikedPosts((prev) => new Set(prev).add(id));
  };

  const loadProfile = useCallback(async () => {
    if (!accessToken || !handle) return;
    setLoading(true);
    setError(null);
    try {
      const res = await axios.get(
        `${API_BASE_URL}/social/users/${encodeURIComponent(handle)}`,
        { headers: { Authorization: `Bearer ${accessToken}` } }
      );
      setProfile(res.data);
    } catch (err) {
      setError(
        err.response?.data?.message || err.message || "User not found"
      );
      setProfile(null);
    } finally {
      setLoading(false);
    }
  }, [accessToken, handle]);

  const loadOrgRelation = useCallback(async () => {
    if (!accessToken || !handle || !hasOrgSelected) {
      setOrgRelation(null);
      return;
    }
    try {
      const res = await axios.get(
        `${API_BASE_URL}/org/${encodeURIComponent(selectedOrgId)}/social/users/${encodeURIComponent(handle)}`,
        { headers: { Authorization: `Bearer ${accessToken}` } }
      );
      setOrgRelation(res.data);
    } catch (err) {
      console.error(err);
      setOrgRelation({ is_following: false, can_follow: false });
    }
  }, [accessToken, handle, hasOrgSelected, selectedOrgId]);

  useEffect(() => {
    loadProfile();
  }, [loadProfile]);

  useEffect(() => {
    loadOrgRelation();
  }, [loadOrgRelation]);

  const personalSocialSelf = Boolean(
    profile?.is_self && isSocialWorkspace
  );
  const showFollowAsOrg = hasOrgSelected;
  const isFollowing = showFollowAsOrg
    ? Boolean(orgRelation?.is_following)
    : Boolean(profile?.is_following);
  const canFollow = showFollowAsOrg
    ? Boolean(orgRelation?.can_follow)
    : true;

  const loadPosts = useCallback(
    async (offset = 0, append = false) => {
      if (!accessToken || !handle) return;
      setPostsLoading(true);
      setPostsError(null);
      try {
        const res = await axios.get(
          `${API_BASE_URL}/social/users/${encodeURIComponent(handle)}/posts`,
          {
            headers: { Authorization: `Bearer ${accessToken}` },
            params: { limit: 30, offset },
          }
        );
        const raw = Array.isArray(res.data?.posts) ? res.data.posts : [];
        const next = raw.map(normalizePost);
        setPosts((prev) => (append ? [...prev, ...next] : next));
        setLikedPosts((prev) =>
          append ? mergeLikedFromPosts(prev, next) : seedLikedFromPosts(next)
        );
        setHasMore(Boolean(res.data?.has_more));
      } catch (err) {
        setPostsError(
          err.response?.data?.message || err.message || "Failed to load posts"
        );
        if (!append) setPosts([]);
      } finally {
        setPostsLoading(false);
      }
    },
    [accessToken, handle, normalizePost]
  );

  useEffect(() => {
    if (profile?.username) {
      loadPosts(0, false);
    }
  }, [profile?.username, loadPosts]);

  useEffect(() => {
    const tab = searchParams.get("tab");
    if (tab === "saved" || tab === "drafts" || tab === "posts") {
      setActiveTab(tab);
    }
  }, [searchParams]);

  const setTab = (value) => {
    setActiveTab(value);
    const next = new URLSearchParams(searchParams);
    if (value === "posts") next.delete("tab");
    else next.set("tab", value);
    setSearchParams(next, { replace: true });
  };

  useEffect(() => {
    if (personalSocialSelf) return;
    if (activeTab === "saved" || activeTab === "drafts") {
      setTab("posts");
    }
    // setTab is stable enough for this guard; avoid looping on searchParams writes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [personalSocialSelf, activeTab]);

  useEffect(() => {
    if (!personalSocialSelf || activeTab !== "saved" || !accessToken) return;
    let cancelled = false;
    (async () => {
      setSavedPostsLoading(true);
      setSavedPostsError(null);
      try {
        const result = await listSocialSavedPosts(accessToken, {
          limit: 20,
          offset: 0,
        });
        if (!cancelled) {
          setSavedPosts((result.posts || []).map(normalizePost));
          setSavedPostsTotal(Number(result.total) || 0);
        }
      } catch (err) {
        if (!cancelled) {
          setSavedPosts([]);
          setSavedPostsTotal(0);
          setSavedPostsError(
            err.response?.data?.message || "Could not load saved posts."
          );
        }
      } finally {
        if (!cancelled) setSavedPostsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [personalSocialSelf, activeTab, accessToken, normalizePost]);

  useEffect(() => {
    if (!personalSocialSelf || activeTab !== "drafts" || !accessToken) return;
    let cancelled = false;
    (async () => {
      setDraftsLoading(true);
      setDraftsError(null);
      try {
        const res = await axios.get(`${API_BASE_URL}/social/me/drafts`, {
          headers: { Authorization: `Bearer ${accessToken}` },
          params: { limit: 50, offset: 0 },
        });
        if (!cancelled) {
          setDrafts(res.data?.posts || []);
          setDraftsTotal(Number(res.data?.total) || 0);
        }
      } catch (err) {
        if (!cancelled) {
          setDrafts([]);
          setDraftsTotal(0);
          setDraftsError(
            err.response?.data?.message || "Could not load drafts."
          );
        }
      } finally {
        if (!cancelled) setDraftsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [personalSocialSelf, activeTab, accessToken]);

  const editingDraft = useMemo(
    () =>
      drafts.find(
        (d) =>
          String(d.social_post_id ?? d.post_id ?? d.postId) ===
          String(editingDraftId)
      ) || null,
    [drafts, editingDraftId]
  );

  const refreshDrafts = async () => {
    if (!accessToken) return;
    const res = await axios.get(`${API_BASE_URL}/social/me/drafts`, {
      headers: { Authorization: `Bearer ${accessToken}` },
      params: { limit: 50, offset: 0 },
    });
    setDrafts(res.data?.posts || []);
    setDraftsTotal(Number(res.data?.total) || 0);
  };

  const handleDraftAutosave = async (payload) => {
    if (!editingDraftId || !accessToken) return;
    await axios.patch(
      `${API_BASE_URL}/social/posts/${editingDraftId}`,
      {
        title: payload.title,
        content: payload.content,
      },
      { headers: { Authorization: `Bearer ${accessToken}` } }
    );
    if (payload.files?.length) {
      try {
        for (const file of payload.files) {
          await uploadSocialPostMedia(accessToken, editingDraftId, file);
        }
      } catch (mediaErr) {
        toastError(mediaErr, "Draft saved, but media upload failed");
      }
    }
    if (payload.embedUrl) {
      try {
        await setSocialEmbed(accessToken, editingDraftId, payload.embedUrl);
      } catch (embedErr) {
        toastError(embedErr, "Draft saved, but video link failed");
      }
    }
    await refreshDrafts();
  };

  const handleDraftRemoveMedia = async (index) => {
    if (!editingDraftId || !accessToken) return;
    await removeSocialPostMedia(accessToken, editingDraftId, index);
    await refreshDrafts();
  };

  const handleDraftPublish = async (payload) => {
    if (!editingDraftId || !accessToken) return;
    setDraftActionLoading(true);
    try {
      await handleDraftAutosave(payload);
      const { data: published } = await axios.post(
        `${API_BASE_URL}/social/posts/${editingDraftId}/publish`,
        {},
        { headers: { Authorization: `Bearer ${accessToken}` } }
      );
      toastSuccess("Draft published");
      setEditingDraftId(null);
      await refreshDrafts();
      const postId =
        published?.social_post_id ?? published?.post_id ?? published?.postId;
      if (postId != null) {
        navigate(permalinkForSocialPost({ postId }));
      }
    } catch (err) {
      toastError(err, "Failed to publish draft");
      throw err;
    } finally {
      setDraftActionLoading(false);
    }
  };

  const handleDraftDiscard = async () => {
    if (!editingDraftId || !accessToken) return;
    const ok = await confirm({
      title: "Discard draft?",
      description: "Discard this draft? This cannot be undone.",
      confirmLabel: "Discard",
      variant: "destructive",
    });
    if (!ok) return;
    setDraftActionLoading(true);
    try {
      await axios.delete(`${API_BASE_URL}/social/posts/${editingDraftId}`, {
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      toastSuccess("Draft discarded");
      setEditingDraftId(null);
      await refreshDrafts();
    } catch (err) {
      toastError(err, "Failed to discard draft");
    } finally {
      setDraftActionLoading(false);
    }
  };

  const handleSavedChange = (postId, saved) => {
    if (saved) return;
    const id = String(postId);
    setSavedPosts((prev) =>
      prev.filter(
        (p) => String(p.social_post_id ?? p.post_id ?? p.postId) !== id
      )
    );
    setSavedPostsTotal((n) => Math.max(0, n - 1));
  };

  const handleLoadMoreSaved = async () => {
    if (
      !accessToken ||
      savedPostsLoading ||
      savedPosts.length >= savedPostsTotal
    ) {
      return;
    }
    setSavedPostsLoading(true);
    try {
      const result = await listSocialSavedPosts(accessToken, {
        limit: 20,
        offset: savedPosts.length,
      });
      setSavedPosts((prev) => [
        ...prev,
        ...(result.posts || []).map(normalizePost),
      ]);
      setSavedPostsTotal(Number(result.total) || 0);
    } catch (err) {
      setSavedPostsError(
        err.response?.data?.message || "Could not load more saved posts."
      );
    } finally {
      setSavedPostsLoading(false);
    }
  };

  // Settings → /u/me?edit=1 opens the edit modal for self (Circa Social workspace only).
  useEffect(() => {
    if (!personalSocialSelf) return;
    if (searchParams.get("edit") !== "1") return;
    setEditOpen(true);
    const next = new URLSearchParams(searchParams);
    next.delete("edit");
    setSearchParams(next, { replace: true });
  }, [personalSocialSelf, searchParams, setSearchParams]);

  const toggleFollow = async () => {
    if (!profile?.username) return;
    if (personalSocialSelf) return;
    if (showFollowAsOrg && !canFollow) return;
    setBusy(true);
    try {
      if (showFollowAsOrg) {
        const url = `${API_BASE_URL}/org/${encodeURIComponent(
          selectedOrgId
        )}/social/follow/users/${encodeURIComponent(profile.username)}`;
        if (isFollowing) {
          await axios.delete(url, {
            headers: { Authorization: `Bearer ${accessToken}` },
          });
          setOrgRelation((prev) => ({
            ...(prev || {}),
            is_following: false,
            can_follow: true,
          }));
        } else {
          await axios.post(
            url,
            {},
            { headers: { Authorization: `Bearer ${accessToken}` } }
          );
          setOrgRelation((prev) => ({
            ...(prev || {}),
            is_following: true,
            can_follow: true,
          }));
        }
        toastSuccess(isFollowing ? "Unfollowed" : "Following");
      } else {
        const url = `${API_BASE_URL}/social/users/${encodeURIComponent(
          profile.username
        )}/follow`;
        const res = isFollowing
          ? await axios.delete(url, {
              headers: { Authorization: `Bearer ${accessToken}` },
            })
          : await axios.post(
              url,
              {},
              { headers: { Authorization: `Bearer ${accessToken}` } }
            );
        setProfile(res.data);
        toastSuccess(isFollowing ? "Unfollowed" : "Following");
      }
    } catch (err) {
      toastError(
        err.response?.data?.message || err.message || "Could not update follow"
      );
    } finally {
      setBusy(false);
    }
  };

  const bannerSrc = resolveAvatarUrl(profile?.banner_url);
  const avatarSrc = resolveAvatarUrl(profile?.avatar_url);
  const initials = useMemo(() => {
    const name = profile?.display_name || profile?.username || "?";
    return name.slice(0, 2).toUpperCase();
  }, [profile?.display_name, profile?.username]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[40vh]">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
      </div>
    );
  }

  if (error || !profile) {
    return (
      <div className="mx-auto max-w-lg px-4 py-12">
        <Alert variant="destructive">
          <AlertDescription>{error || "User not found"}</AlertDescription>
        </Alert>
        <Button asChild variant="outline" className="mt-4">
          <Link to={paths.explore()}>Back to Explore</Link>
        </Button>
      </div>
    );
  }

  return (
    <div className="min-h-full pb-8 md:pb-16">
      <div className="relative w-full">
        <div
          data-social-banner
          className="relative h-28 w-full overflow-hidden bg-muted sm:h-36 md:h-48"
        >
          {bannerSrc ? (
            <img
              src={bannerSrc}
              alt=""
              className="absolute inset-0 h-full w-full object-cover object-center"
            />
          ) : (
            <div
              className="absolute inset-0"
              style={{
                background:
                  "linear-gradient(135deg, hsl(var(--muted)) 0%, hsl(var(--primary) / 0.35) 100%)",
              }}
            />
          )}
        </div>
        <div className="mx-auto max-w-2xl min-w-0 px-4">
          <div className="relative -mt-12 flex min-w-0 flex-wrap items-end justify-between gap-4">
            <Avatar className="h-24 w-24 shrink-0 border-4 border-background shadow-md sm:h-28 sm:w-28">
              {avatarSrc ? (
                <AvatarImage src={avatarSrc} alt="" />
              ) : null}
              <AvatarFallback className="text-xl font-semibold">
                {initials}
              </AvatarFallback>
            </Avatar>
            <div className="pb-1">
              {personalSocialSelf ? (
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setEditOpen(true)}
                >
                  Edit profile
                </Button>
              ) : showFollowAsOrg && !canFollow ? (
                <p className="text-xs text-muted-foreground max-w-[14rem] text-right">
                  Viewing as organization. Follow requires Social publish.
                </p>
              ) : (
                <Button
                  type="button"
                  variant={isFollowing ? "outline" : "default"}
                  disabled={busy || (showFollowAsOrg && !canFollow)}
                  onClick={toggleFollow}
                >
                  {busy ? "…" : isFollowing ? "Following" : "Follow"}
                </Button>
              )}
            </div>
          </div>

          <div className="mt-3 min-w-0 space-y-2">
            <h1 className="truncate text-2xl font-semibold tracking-tight" title={profile.display_name}>
              {profile.display_name}
            </h1>
            <p className="truncate text-muted-foreground" title={`@${profile.username}`}>
              @{profile.username}
            </p>
            {profile.bio ? (
              <p className="text-sm whitespace-pre-wrap">{profile.bio}</p>
            ) : null}
            <p className="text-sm text-muted-foreground">
              {personalSocialSelf ? (
                <button
                  type="button"
                  className="hover:underline"
                  onClick={() => setFollowListMode("following")}
                >
                  <span className="font-medium text-foreground">
                    {profile.following_count ?? 0}
                  </span>{" "}
                  Following
                </button>
              ) : (
                <>
                  <span className="font-medium text-foreground">
                    {profile.following_count ?? 0}
                  </span>{" "}
                  Following
                </>
              )}
              <span className="mx-2">·</span>
              {personalSocialSelf ? (
                <button
                  type="button"
                  className="hover:underline"
                  onClick={() => setFollowListMode("followers")}
                >
                  <span className="font-medium text-foreground">
                    {profile.followers_count ?? 0}
                  </span>{" "}
                  Followers
                </button>
              ) : (
                <>
                  <span className="font-medium text-foreground">
                    {profile.followers_count ?? 0}
                  </span>{" "}
                  Followers
                </>
              )}
              <span className="mx-2">·</span>
              <span className="font-medium text-foreground">
                {profile.posts_count ?? 0}
              </span>{" "}
              Posts
            </p>
          </div>
        </div>
      </div>

      <div className="mx-auto max-w-2xl min-w-0 px-4 mt-4 sm:mt-8">
        <Tabs value={activeTab} onValueChange={setTab}>
          <TabsList className="mb-4 max-w-full flex-wrap h-auto">
            <TabsTrigger value="posts">Posts</TabsTrigger>
            {personalSocialSelf ? (
              <>
                <TabsTrigger value="saved">Saved</TabsTrigger>
                <TabsTrigger value="drafts">Drafts</TabsTrigger>
              </>
            ) : null}
          </TabsList>

          <TabsContent value="posts" className="space-y-4 mt-0">
        {postsError ? (
          <Alert variant="destructive" className="mb-4">
            <AlertDescription>{postsError}</AlertDescription>
          </Alert>
        ) : null}

        {postsLoading && posts.length === 0 ? (
          <div className="flex justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
          </div>
        ) : posts.length === 0 ? (
          <p className="py-12 text-center text-sm text-muted-foreground">
            No posts yet.
          </p>
        ) : (
          <div className="space-y-4">
            {posts.map((post) => (
              <PostCard
                key={post.post_id}
                post={post}
                currentUserId={user?.user_id}
                authorAboveTitle
                socialHome
                relativeTimestamps
                socialTombstone
                socialRepost
                accessToken={accessToken}
                isLiked={likedPosts.has(
                  String(
                    post.repost_kind === "bare" && post.repost_of
                      ? post.repost_of.social_post_id ?? post.repost_of.post_id
                      : post.post_id
                  )
                )}
                onExpand={() =>
                  openThread(
                    post.repost_kind === "bare" && post.repost_of
                      ? post.repost_of
                      : post
                  )
                }
                onQuoteCreated={(created) => {
                  if (created) setPosts((prev) => [normalizePost(created), ...prev]);
                }}
                onRepostChange={(info) => {
                  if (info?.unreposted && info.root_id != null) {
                    setPosts((prev) =>
                      prev.filter(
                        (p) =>
                          !(
                            p.repost_kind === "bare" &&
                            String(
                              p.repost_of_social_post_id ??
                                p.repost_of?.post_id
                            ) === String(info.root_id)
                          )
                      )
                    );
                  }
                  if (info?.post && !info.unreposted) {
                    setPosts((prev) => [normalizePost(info.post), ...prev]);
                  }
                }}
                onEmbedLike={async () => {
                  const root = quoteEmbedRoot(post);
                  if (!root) return;
                  const postId = root.social_post_id ?? root.post_id;
                  try {
                    await toggleSocialLike(postId);
                  } catch (err) {
                    toastError(
                      err.response?.data?.message ||
                        err.message ||
                        "Could not update like"
                    );
                  }
                }}
                onEmbedExpand={() => {
                  const root = quoteEmbedRoot(post);
                  if (root) openThread(root);
                }}
                embedIsLiked={(() => {
                  const root = quoteEmbedRoot(post);
                  return root
                    ? likedPosts.has(
                        String(root.social_post_id ?? root.post_id)
                      )
                    : false;
                })()}
                onLike={async () => {
                  const postId =
                    post.repost_kind === "bare" && post.repost_of
                      ? post.repost_of.social_post_id ?? post.repost_of.post_id
                      : post.post_id;
                  const id = String(postId);
                  const already = likedPosts.has(id);
                  const bump = (delta) => {
                    setPosts((prev) =>
                      prev.map((p) => {
                        const pid = String(p.post_id);
                        const rid = p.repost_of
                          ? String(
                              p.repost_of.social_post_id ?? p.repost_of.post_id
                            )
                          : null;
                        if (pid === id) {
                          return {
                            ...p,
                            like_count: Math.max(0, (p.like_count || 0) + delta),
                          };
                        }
                        if (rid === id && p.repost_of) {
                          return {
                            ...p,
                            repost_of: {
                              ...p.repost_of,
                              like_count: Math.max(
                                0,
                                (p.repost_of.like_count || 0) + delta
                              ),
                            },
                          };
                        }
                        return p;
                      })
                    );
                  };
                  try {
                    await toggleSocialLike(postId);
                    if (!(writeApis.isActAsOrg && already)) {
                      bump(already ? -1 : 1);
                    }
                  } catch (err) {
                    toastError(
                      err.response?.data?.message ||
                        err.message ||
                        "Could not update like"
                    );
                  }
                }}
              />
            ))}
            {hasMore ? (
              <div className="flex justify-center pt-2">
                <Button
                  type="button"
                  variant="outline"
                  disabled={postsLoading}
                  onClick={() => loadPosts(posts.length, true)}
                >
                  {postsLoading ? "Loading…" : "Load more"}
                </Button>
              </div>
            ) : null}
          </div>
        )}
          </TabsContent>

          {personalSocialSelf ? (
            <TabsContent value="saved" className="mt-0">
              <Card>
                <CardHeader>
                  <CardTitle>Saved Posts</CardTitle>
                  <CardDescription>
                    Posts you saved on Circa Social, newest first.
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  {savedPostsError ? (
                    <Alert variant="destructive">
                      <AlertDescription>{savedPostsError}</AlertDescription>
                    </Alert>
                  ) : savedPostsLoading && savedPosts.length === 0 ? (
                    <p className="text-sm text-muted-foreground">
                      Loading saved posts…
                    </p>
                  ) : savedPosts.length === 0 ? (
                    <p className="text-sm text-muted-foreground">
                      You haven&apos;t saved any posts yet.
                    </p>
                  ) : (
                    <>
                      <p className="text-xs text-muted-foreground">
                        {savedPostsTotal}{" "}
                        {savedPostsTotal === 1 ? "saved post" : "saved posts"}
                      </p>
                      <div className="space-y-4">
                        {savedPosts.map((post) => (
                          <PostCard
                            key={post.post_id}
                            post={post}
                            socialRepost
                            socialHome
                            relativeTimestamps
                            socialTombstone
                            accessToken={accessToken}
                            onExpand={() => openThread(post)}
                            onSavedChange={handleSavedChange}
                          />
                        ))}
                      </div>
                      {savedPosts.length < savedPostsTotal ? (
                        <div className="flex justify-center pt-1">
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            disabled={savedPostsLoading}
                            onClick={handleLoadMoreSaved}
                          >
                            {savedPostsLoading ? "Loading…" : "Load more"}
                          </Button>
                        </div>
                      ) : null}
                    </>
                  )}
                </CardContent>
              </Card>
            </TabsContent>
          ) : null}

          {personalSocialSelf ? (
            <TabsContent value="drafts" className="mt-0">
              <Card>
                <CardHeader>
                  <CardTitle>Draft Posts</CardTitle>
                  <CardDescription>
                    Saved drafts — only you can see these until you publish.
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  {draftsLoading && drafts.length === 0 ? (
                    <p className="text-sm text-muted-foreground">
                      Loading drafts…
                    </p>
                  ) : draftsError ? (
                    <Alert variant="destructive">
                      <AlertDescription>{draftsError}</AlertDescription>
                    </Alert>
                  ) : editingDraft ? (
                    <div className="space-y-4">
                      <div className="flex flex-wrap items-center justify-between gap-2">
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => setEditingDraftId(null)}
                          disabled={draftActionLoading}
                        >
                          ← Back to drafts
                        </Button>
                        <Button
                          type="button"
                          variant="destructive"
                          size="sm"
                          onClick={handleDraftDiscard}
                          disabled={draftActionLoading}
                        >
                          Discard
                        </Button>
                      </div>
                      <p className="text-xs text-muted-foreground">
                        {editingDraft.community?.name
                          ? `Community: ${editingDraft.community.name}`
                          : "Personal post"}{" "}
                        · Changes autosave while you edit.
                      </p>
                      <PostComposer
                        mode="expanded"
                        destinationMode="fixed"
                        hideDestination
                        hideTags
                        inferTitle={false}
                        fixedTeamId="personal"
                        fixedTeamLabel={
                          editingDraft.community?.name || "Your followers"
                        }
                        isSubmitting={draftActionLoading}
                        onSubmit={handleDraftPublish}
                        onSaveDraft={handleDraftAutosave}
                        onRemoveMedia={handleDraftRemoveMedia}
                        autosave
                        submitLabel="Publish"
                        formKey={draftComposerKey}
                        initialTitle={editingDraft.title || ""}
                        initialContent={editingDraft.content || ""}
                        initialMedia={
                          Array.isArray(editingDraft.media)
                            ? editingDraft.media.map((m) =>
                                m?.url ? { ...m, url: resolveMediaUrl(m.url) } : m
                              )
                            : []
                        }
                        embedded
                        allowCritical={false}
                      />
                    </div>
                  ) : drafts.length === 0 ? (
                    <div className="rounded-lg border border-dashed border-border px-4 py-10 text-center space-y-2">
                      <p className="text-sm font-medium text-foreground">
                        No drafts yet
                      </p>
                      <p className="text-sm text-muted-foreground max-w-md mx-auto">
                        When you save a post as a draft from Home or a community,
                        it will show up here.
                      </p>
                    </div>
                  ) : (
                    <ul className="divide-y divide-border rounded-md border border-border">
                      {drafts.map((draft) => {
                        const id =
                          draft.social_post_id ?? draft.post_id ?? draft.postId;
                        const destLabel = draft.community?.name
                          ? draft.community.name
                          : "Personal";
                        const updated =
                          draft.draft_updated_at ||
                          draft.draftUpdatedAt ||
                          draft.updated_at;
                        const titlePlain = markdownToPlain(draft.title || "");
                        const contentPlain = markdownToPlain(
                          draft.content || ""
                        );
                        const excerpt =
                          contentPlain.slice(0, 120) ||
                          titlePlain ||
                          "Empty draft";
                        const listTitle =
                          titlePlain ||
                          contentPlain.slice(0, 60) ||
                          "Draft";
                        return (
                          <li key={id}>
                            <button
                              type="button"
                              className="w-full text-left px-4 py-3 hover:bg-muted/40 transition-colors"
                              onClick={() => {
                                setEditingDraftId(id);
                                setDraftComposerKey((k) => k + 1);
                              }}
                            >
                              <p className="text-sm font-medium truncate">
                                {listTitle}
                              </p>
                              <p className="text-xs text-muted-foreground mt-0.5 line-clamp-2">
                                {excerpt}
                              </p>
                              <p className="text-[11px] text-muted-foreground mt-1">
                                {destLabel}
                                {updated
                                  ? ` · Updated ${new Date(updated).toLocaleString()}`
                                  : ""}
                              </p>
                            </button>
                          </li>
                        );
                      })}
                    </ul>
                  )}
                  {draftsTotal > 0 && !editingDraft ? (
                    <p className="text-xs text-muted-foreground mt-3">
                      {draftsTotal} draft{draftsTotal === 1 ? "" : "s"}
                    </p>
                  ) : null}
                </CardContent>
              </Card>
            </TabsContent>
          ) : null}
        </Tabs>
      </div>

      {personalSocialSelf ? (
        <>
          <SocialEditProfileDialog
            open={editOpen}
            onOpenChange={setEditOpen}
            accessToken={accessToken}
            profile={profile}
            onSaved={(next) => {
              if (next)
                setProfile((prev) => ({ ...prev, ...next, is_self: true }));
            }}
          />
          <SocialFollowListDialog
            open={Boolean(followListMode)}
            onOpenChange={(next) => {
              if (!next) setFollowListMode(null);
            }}
            mode={followListMode || "following"}
            handle={profile.username}
            accessToken={accessToken}
            onFollowingCountDelta={(delta) => {
              setProfile((prev) =>
                prev
                  ? {
                      ...prev,
                      following_count: Math.max(
                        0,
                        (prev.following_count ?? 0) + delta
                      ),
                    }
                  : prev
              );
            }}
          />
        </>
      ) : null}
    </div>
  );
};

export default SocialProfilePage;
